import { normalizeMessageText } from './message-text';
import { enabledServices, listPlatforms, normalizeService, platformHint, poolFilters } from './platforms';
export { PLATFORM_CATALOG, applyRemoteCatalog, enabledPlatforms, isServiceEnabled, listPlatforms, mergeServiceMeta, normalizeService, subscribePlatforms, usePlatforms, } from './platforms';
/** 目前可操作的站。常數只是初始快照；互動請用 `enabledServices()`。 */
export const SERVICES = enabledServices();
/**
 * 服務 → 顯示名稱。**用 Proxy 讀目錄**（不是快照）：後端補了 `/api/platforms`、
 * 或目錄新增一個站之後，`SERVICE_LABELS['wootalk']` 立刻查得到，不會是 undefined。
 */
export const SERVICE_LABELS = new Proxy({}, {
    get: (_target, key) => (typeof key === 'string' ? serviceLabel(key) : undefined),
    has: (_target, key) => typeof key === 'string',
    ownKeys: () => listPlatforms().map((item) => item.id),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
});
/** 各站的註冊限制（同樣是 Proxy，跟著目錄走）。 */
export const POOL_SERVICE_HINTS = new Proxy({}, {
    get: (_target, key) => (typeof key === 'string' ? platformHint(key) ?? '' : ''),
});
/** 服務 → 顯示名稱；未知的站回傳 id 本身，不會變成 undefined。 */
export function serviceLabel(service) {
    if (!service)
        return '';
    return platformLabels()[service] ?? service;
}
export function platformLabels() {
    const out = {};
    for (const p of listPlatforms())
        out[p.id] = p.label;
    return out;
}
/** 篩選按鈕（`all` ＋ 可操作的站）；目錄更新後跟著變。 */
export function getPoolFilters() {
    return poolFilters();
}
export const POOL_FILTERS = poolFilters();
/**
 * 各服務的註冊說明。只寫「操作上會撞到的限制」，不要抄整份規格：
 * UT 進房後暱稱由站方決定，跟 ek21 不一樣，使用者最容易在這裡誤會。
 */
export function serviceHint(service) {
    return platformHint(service) ?? '';
}
/**
 * `lastMessagePreview` 可能是後端直接給的字串（沒經過前端事件處理），一樣可能是
 * 站方 JSON 系統訊息或含 `img:` 標記。清單顯示前先過同一套正規化；空字串視為沒有預覽。
 */
function normalizePreview(value) {
    if (typeof value !== 'string' || value === '')
        return null;
    const text = normalizeMessageText(value);
    return text === '' ? null : text;
}
/** 把 Worker 的寬鬆 JSON 收斂成 Conversation；缺欄位補安全預設，不讓一列壞掉整份空白。 */
export function toConversation(raw) {
    const account = toAccount(raw);
    const num = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
    return {
        ...account,
        unread: num(raw.unread) ?? 0,
        lastActivityAt: num(raw.lastActivityAt),
        lastMessagePreview: normalizePreview(raw.lastMessagePreview),
        roomOnline: num(raw.roomOnline),
        lastReadTs: num(raw.lastReadTs) ?? 0,
        ...(typeof raw.rateLimited === 'boolean' ? { rateLimited: raw.rateLimited } : {}),
        ...(num(raw.cooldownUntil) !== null ? { cooldownUntil: num(raw.cooldownUntil) } : {}),
        ...(typeof raw.lastError === 'string' && raw.lastError !== '' ? { lastError: raw.lastError } : {}),
    };
}
/** 一次最多註冊幾顆（後端 `MAX_REGISTER_PER_REQUEST`）；UI 只給 1–3。 */
export const MAX_POOL_REGISTER = 3;
/** 兩批註冊之間的最小間隔（後端 `REGISTER_BATCH_GAP_MS`）：UI 拿它做倒數。 */
export const POOL_REGISTER_GAP_MS = 20_000;
/**
 * 把 `GET /api/accounts` 的寬鬆 JSON 收斂成 `PoolAccount`；壞一列不讓整份空白。
 *
 * **逐欄明確挑選**（不用 spread：原始 JSON 裡可能有 password／token 內容），
 * 而且只搬白名單裡的欄位。缺欄位或型別不對就**不寫 key**（畫面 `—`），
 * 但後端明確送 `null` 的欄位（`tokenSavedAt` / `lastUsedAt` / `lastRebuildAt` /
 * `source`）要保留 `null`——那不是「沒回報」，是「確定沒有」，
 * 「未使用過」的篩選靠的就是 `lastUsedAt === null`。
 */
export function toPoolAccount(raw) {
    const src = { ...raw };
    const num = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
    const bool = (value) => typeof value === 'boolean' ? value : undefined;
    /** 有這個 key 才看：缺 → undefined（沒回報）；明確 null → null（確定沒有）。 */
    const nullableNum = (key) => {
        if (!(key in src))
            return undefined;
        return src[key] === null ? null : (num(src[key]) ?? undefined);
    };
    const account = {
        service: normalizeService(src.service),
        username: String(src.username ?? ''),
        createdAt: num(src.createdAt) ?? 0,
        lastLoginAt: num(src.lastLoginAt),
        // 後端只有明確 false 才代表「登入失敗」；缺欄位視為可用。
        ok: src.ok !== false,
    };
    // §4.2 擴充欄位：逐欄明確搬一行（新增欄位就在這裡加，不要改成 spread）。
    const hasToken = bool(src.hasToken);
    if (hasToken !== undefined)
        account.hasToken = hasToken;
    const held = bool(src.held);
    if (held !== undefined)
        account.held = held;
    // 顯示名稱：字串才有值；後端明確 null 就保留 null（＝確定未命名）。
    if (src.displayName === null)
        account.displayName = null;
    else if (typeof src.displayName === 'string' && src.displayName.trim() !== '') {
        account.displayName = src.displayName.trim();
    }
    const tokenValid = src.tokenValid === null ? null : bool(src.tokenValid);
    if (tokenValid !== undefined)
        account.tokenValid = tokenValid;
    const rebuilds = num(src.rebuilds);
    if (rebuilds !== null)
        account.rebuilds = rebuilds;
    const relogins = num(src.relogins);
    if (relogins !== null)
        account.relogins = relogins;
    const usageCount = num(src.usageCount);
    if (usageCount !== null)
        account.usageCount = usageCount;
    // 這幾個「可能明確是 null」，null 要保留（見上方說明）。
    const tokenSavedAt = nullableNum('tokenSavedAt');
    if (tokenSavedAt !== undefined)
        account.tokenSavedAt = tokenSavedAt;
    const lastUsedAt = nullableNum('lastUsedAt');
    if (lastUsedAt !== undefined)
        account.lastUsedAt = lastUsedAt;
    const lastRebuildAt = nullableNum('lastRebuildAt');
    if (lastRebuildAt !== undefined)
        account.lastRebuildAt = lastRebuildAt;
    // 來源只認白名單：明確 null 保留 null（舊資料），其他怪值當「沒回報」。
    if (src.source === null)
        account.source = null;
    else if (src.source === 'console' || src.source === 'grow_pool' || src.source === 'manual') {
        account.source = src.source;
    }
    if (typeof src.heldBySession === 'string' && src.heldBySession !== '') {
        account.heldBySession = src.heldBySession;
    }
    return account;
}
/** 把 Worker 的 AgentState（寬鬆 JSON）收斂成 Account。 */
export function toAccount(raw) {
    const service = normalizeService(raw.service);
    return {
        id: String(raw.id ?? ''),
        service,
        nickname: String(raw.nickname ?? ''),
        room: typeof raw.room === 'string' && raw.room !== '' ? raw.room : null,
        roomNo: typeof raw.roomNo === 'string' && raw.roomNo !== '' ? raw.roomNo : null,
        online: raw.online === true,
        ...(raw.dormant === true ? { dormant: true } : {}),
        createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : undefined,
        eventCount: typeof raw.eventCount === 'number' ? raw.eventCount : undefined,
    };
}
/** 暱稱可用的字元集：只有小寫英文與數字。ek21 的 `/^[\u3400-\u9fffA-Za-z0-9~]+$/` 不接受底線，所以這裡不產生。 */
const NICKNAME_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
/** 本次連線已產生過的暱稱，避免同一個瀏覽器分頁內連續建帳號時自己撞自己。 */
const USED_NICKNAMES = new Set();
const NICKNAME_MAX_LENGTH = 10;
/**
 * 產生 `前綴 + 時間戳後 5 位 + 2 位隨機` 的暱稱（例：`rea7k2m9x4f`，總長 ≤10）。
 *
 * 只用小寫英數，避免踩到 ek21 暱稱規則（不可底線／空白／符號）。
 * 舊版是 `rea` + 6 位純隨機，36^6 分之一看起來夠，但同一秒連按很容易撞；
 * 現在以毫秒時間戳保證跨呼叫遞增，再用遞增偏移＋已用集合保證**這條連線內絕不重複**。
 * 隨機源可注入，方便測試。
 */
export function randomNickname(prefix = 'rea', random = Math.random) {
    const head = prefix.slice(0, NICKNAME_MAX_LENGTH);
    for (let attempt = 0; attempt < 200; attempt += 1) {
        // 毫秒時間戳 → base36，取後 5 位；連按時以 attempt 遞增推開。
        const stamp = (Date.now() + attempt).toString(36).slice(-5).padStart(5, '0');
        let rand = '';
        for (let i = 0; i < 2; i += 1) {
            rand += NICKNAME_ALPHABET[Math.floor(random() * NICKNAME_ALPHABET.length)] ?? '0';
        }
        const nickname = `${head}${stamp}${rand}`.slice(0, NICKNAME_MAX_LENGTH);
        if (!USED_NICKNAMES.has(nickname)) {
            USED_NICKNAMES.add(nickname);
            return nickname;
        }
    }
    // 200 次都撞（等於時間倒退＋隨機源壞掉）：退回純亂數，至少不會回傳重複字串。
    let fallback = head;
    while (fallback.length < NICKNAME_MAX_LENGTH) {
        fallback += NICKNAME_ALPHABET[Math.floor(random() * NICKNAME_ALPHABET.length)] ?? '0';
    }
    USED_NICKNAMES.add(fallback);
    return fallback;
}
/** 由 sender／帳號名決定穩定色票索引（純函式，SSR 安全）。 */
export function senderColorIndex(seed, buckets = 8) {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1)
        hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
    return Math.abs(hash) % buckets;
}
/* ==========================================================================
 * 帳號顯示名稱（displayName）
 *
 * 規則：有 displayName 就用它；沒有就顯示「EK21-3f2a」這種縮寫（服務 + 帳號末 4 碼）。
 * 為什麼取**末 4 碼**而不是前 4 碼：ek21／UT 帳號都是 `rea` 開頭（`generateUsername()`），
 * 前 4 碼每顆都一樣，等於看不出差別。末 4 碼才有區辨力。
 * **任何畫面都不得顯示完整 username**（使用者原話：「一串路碼」）。
 * ========================================================================== */
/** 帳號縮寫：`EK21-3f2a`／`UT-a91c`（服務名 + username 末 4 碼）。 */
export function shortAccountId(service, username) {
    const tail = username.slice(-4);
    // 一律大寫：畫面上「EK21-3f2a」比「ek21-3f2a」更容易跟帳號本身分開。
    return `${serviceLabel(service).toUpperCase()}-${tail}`;
}
/**
 * 一行顯示用的帳號標籤：`displayName` 優先，沒有就 `未命名（EK21-3f2a）`。
 * `withFallback:false` 時只回縮寫本身（聊天室 sender 用，避免每句都掛「未命名」）。
 */
export function accountLabel(service, username, displayName, options = {}) {
    const name = typeof displayName === 'string' ? displayName.trim() : '';
    if (name !== '')
        return name;
    const short = shortAccountId(service, username);
    return options.withFallback === false ? short : `未命名（${short}）`;
}
//# sourceMappingURL=types.js.map